// Libraries
import React, { useEffect, useState } from 'react'
import { ModuleNames } from '../../../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../../../constants/localization.constants'
import Layout from '../../../../layouts/layout'

// New Product Page Modules
import ContentBlock from '../../../../global/contentBlock'
import ContentColumns from '../../../../global/contentColumns'
import GlobalContainer from '../../../../global/globalContainer'
import Hero from '../../../../global/hero'
import MainCTA from '../../../../global/mainCta'
import Pagination from '../../../../global/pagination'
import SEOTags from '../../../../global/seoTags'
import SimpleNav from '../../../../global/simpleNav'
import Tiles from '../../../../global/tiles'
import ListingItem from '../listingItem'
import OfficeRegion from '../officeRegion'

import { findTarget } from '../../../../../utils/microcopy.utils'

// Style
import * as styles from './style'

// Helpers and utils
import Typography from '../../../../global/typography'

import moment from 'moment'
import { OPTIONS } from '../../../../../constants/dateOptions'
import UrlUtil from '../../../../../utils/url.utils'
import { PartnersLocatorFilters as DropdownContainer } from '../../../partners/partnerListings'
import LeadershipBoard from '../leadershipBoard'

import Button from '../../../../global/button'

const CompanyListingPage = ({
  path,
  pageContext,
  location,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {
  let dynamicPageContent: any,
    listingPageContent: any,
    officesPageContent: any,
    companyListingPageHero: any,
    leadershipPageContent: any,
    pageData = pageContext.companyListingPageData || {},
    pageType = 'companyListing',
    localeListings = pageContext.localeListings || [],
    featuredListings = [],
    microcopy = pageData.microcopy || {},
    tabOrder = pageData.tabOrder,
    leadershipBoardData,
    pageNumber = pageContext.pageNumber,
    numberOfPages = pageContext.numberOfPages,
    slug = pageContext.slug

  const locale = pageContext.pageLocale ? pageContext.pageLocale : LocaleCodes.ENGLISH.code

  const LISTING_TYPES = {
    ALL_ARTICLES:
      microcopy.values && (findTarget('AllTAB.text', microcopy.values, false) || 'All Articles'),
    PRESS_RELEASES:
      microcopy.values &&
      (findTarget('PressTAB.text', microcopy.values, false) || 'Press Releases'),
    MEDIA_COVERAGE:
      microcopy.values &&
      (findTarget('MediaTAB.text', microcopy.values, false) || 'Media Coverage'),
  }

  // financialNewsText
  const financialNewsText =
    microcopy.values &&
    (findTarget('FinancialNews.text', microcopy.values, false) || 'Go and Explore')

  const financialNewsLinkText =
    microcopy.values &&
    (findTarget('InvestorRelations.text', microcopy.values, false) || 'Investor Relations')

  const financialNewsLinkUrl =
    microcopy.values && (findTarget('InvestorRelations.url', microcopy.values, false) || '#')

  // used in url query
  const yearTextParameter =
    microcopy.values &&
    (findTarget('YearFILTER.text', microcopy.values, false) || 'year')
      .split(' ')
      .join('-')
      .toLowerCase()

  // used in url query
  const typeTextParameter =
    microcopy.values &&
    (findTarget('TypeURL.text', microcopy.values, false) || 'type')
      .split(' ')
      .join('-')
      .toLowerCase()

  const mobileYearFilterText =
    microcopy.values && (findTarget('AllYears.text', microcopy.values, false) || 'All Years')

  // Pagination
  const listingsPerPage = 6
  //only used internally not by the paging system
  const handlePaginationClick = (page: number) => {
    if (page > 1) {
      history.pushState(null, '', `/${slug}/${page}`)
    } else {
      history.pushState(null, '', `/${slug}`)
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const listingIDsToExlude = new Set()
  if (pageContext.featuredListings && pageContext.featuredListings.length > 0) {
    featuredListings = pageContext.featuredListings.map((listing: any, index: number) => {
      let listingTitle = listing.title || '',
        listingDate = listing.date || '',
        listingSlug = listing.slug || '',
        listingUrl = listing.url1 || '',
        listingType = listing.articleType || LISTING_TYPES.MEDIA_COVERAGE,
        listingPublication =
          listing.metaInformation &&
          listing.metaInformation[0] &&
          listing.metaInformation[0].customerName
            ? listing.metaInformation[0].customerName
            : '',
        formattedDate = ''

      const date = new Date(listingDate)
      if (date) formattedDate = moment(date).utc().format('MMMM DD, YYYY')
      formattedDate = new Date(formattedDate).toLocaleDateString(locale, OPTIONS)
      // Exclude the featured listings from the listings to be displayed
      listingIDsToExlude.add(listing.id)

      return (
        <ListingItem
          key={`FeaturedListItem--${listingSlug}-${index}`}
          {...{
            listingTitle,
            listingSlug,
            listingUrl,
            listingType,
            listingPublication,
            formattedDate,
          }}
        />
      )
    })
  }

  let defaultLocale: string
  if (pageContext && pageContext.pageLocale) {
    defaultLocale = pageContext.pageLocale
  } else {
    defaultLocale = LocaleCodes.ENGLISH.code
  }

  if (pageData && pageData.flexSection) {
    const productPageContent = pageData.flexSection
    dynamicPageContent = productPageContent.map((module: any, i: Number) => {
      switch (module.__typename) {
        case ModuleNames.GLOBAL_CONTENT_BLOCK:
          return <ContentBlock data={module} key={`page-component--${i}`}></ContentBlock>

        case ModuleNames.ANCHOR_LINK:
          const identifier = module?.identifier ? module.identifier : ''
          return <span id={identifier}></span>

        case ModuleNames.HERO:
          const pageTitle = pageData.name || ''
          companyListingPageHero = (
            <Hero
              data={module}
              location={location}
              pageTitle={pageTitle}
              showBreadcrumbs={true}
              key={`page-component--${i}`}
              pageType={pageType}
              metaInformation={pageData.metaInformation}
              pageUseCase={pageData.useCase}
              locale={defaultLocale}
            />
          )
          return null
        case ModuleNames.GLOBAL_MAIN_CTA:
          return <MainCTA data={module} key={`page-component--${i}`}></MainCTA>
        case ModuleNames.TILES:
          return <Tiles data={module} key={`page-component--${i}`}></Tiles>
        case ModuleNames.GLOBAL_CONTENT_COLUMNS:
          return <ContentColumns data={module} key={`page-component--${i}`}></ContentColumns>
      }
    })
  }

  // exclude featured from paginated listings
  localeListings = localeListings.filter((listing: any) => !listingIDsToExlude.has(listing.id))

  // Filters
  const [typeSelection, setTypeSelection] = useState('')
  const [yearSelection, setYearSelection] = useState<string[]>([])
  const [filteredListings, setFilteredListings] = useState(localeListings)

  const gatherAllYears = (listings: any) => {
    const years = new Set()
    listings.forEach((listing: any) => {
      const date = new Date(listing.date)
      if (date) {
        listing.date ? years.add(moment(date).utc().format('YYYY')) : ''
      }
    })
    return Array.from(years)
  }

  const listingYears = gatherAllYears(localeListings)

  const handleYearChange = (e: any) => {
    const removeIdx = yearSelection.indexOf(e.target.value)
    let newYearSelections = []
    if (removeIdx !== -1) {
      const clonedArray = [...yearSelection] // make a separate copy of the array
      clonedArray.splice(removeIdx, 1)
      newYearSelections = clonedArray
    } else {
      newYearSelections = [...yearSelection, e.target.value]
    }
    return newYearSelections
  }

  const handleListFiltering = () => {
    let filtered = localeListings

    if (typeSelection === LISTING_TYPES.ALL_ARTICLES) {
      filtered = filtered
    } else if (
      typeSelection === LISTING_TYPES.PRESS_RELEASES ||
      typeSelection === LISTING_TYPES.MEDIA_COVERAGE
    ) {
      filtered = filtered.filter((listing: any) => {
        if (typeSelection === LISTING_TYPES.PRESS_RELEASES) {
          return listing.articleType === 'Press Release'
        } else {
          // Media Coverage
          return listing.articleType === undefined
        }
      })
    }

    if (yearSelection.length > 0) {
      const yearSet = new Set(yearSelection)
      filtered = filtered.filter((listing: any) => {
        if (listing.date) {
          const listingDate = new Date(listing.date)
          if (listingDate) {
            const listingYear = moment(listingDate).utc().format('YYYY')
            return yearSet.has(listingYear)
          } else {
            return false
          }
        }
      })
    }

    setFilteredListings(filtered)
  }

  const getPageNumber = () => {
    if (typeof window !== 'undefined' && window.location.pathname) {
      const numberInUrl = window.location.pathname.match(/[0-9]+/)
      if (numberInUrl && numberInUrl[0]) {
        return parseInt(numberInUrl[0])
      }
    } else {
      if (pageNumber) {
        return pageNumber
      }
    }
    return 1
  }

  const currentPage = getPageNumber()
  const start = (currentPage - 1) * listingsPerPage
  const end = currentPage * listingsPerPage

  const hrefForPage = (page: number) => {
    let url = ''
    let queryString = typeof window !== 'undefined' ? window.location.search.slice(1) : ''
    if (page === 1) {
      // url = /newsroom
      if (typeof window !== 'undefined') {
        if (queryString) {
          url = `/${defaultSlug}?${queryString}`
        } else {
          url = `/${defaultSlug}`
        }
      } else if (defaultSlug) {
        // SSR
        url = `/${defaultSlug}`
      }
    } else {
      // url = /newrsoom/page?[query]
      if (typeof window !== 'undefined') {
        if (queryString) {
          url = `/${defaultSlug}/${page}?${queryString}`
        } else {
          url = `/${defaultSlug}/${page}`
        }
      } else if (defaultSlug) {
        url = `/${defaultSlug}/${page}`
      }
    }
    return url
  }

  const updateURL = (yearParam: string[], typeParam: string) => {
    let query = ''
    let formattedYear = urlFormattedYearParam(yearParam)
    let formattedType = urlFormattedTypeParam(typeParam)

    // if the type is All Articles, we don't need to add type parameter
    if (typeParam === LISTING_TYPES.ALL_ARTICLES) typeParam = ''
    if (yearParam.length && typeParam) {
      query = `${yearTextParameter}=${formattedYear}&${typeTextParameter}=${formattedType}`
    } else {
      query =
        (yearParam.length && `${yearTextParameter}=${formattedYear}`) ||
        (typeParam && `${typeTextParameter}=${formattedType}`) ||
        ''
    }

    if (typeof window !== `undefined`) {
      if (query == '') {
        history.pushState(null, '', '/' + defaultSlug)
      } else {
        history.pushState('', '', `/${defaultSlug}?${query}`)
      }
    }
  }

  const getQueryParams = () => {
    let yearParam = UrlUtil.getUrlParameter(yearTextParameter)
    let typeParam = UrlUtil.getUrlParameter(typeTextParameter)

    // If the type is not Press Release or Media Coverage, ignore it
    if (urlFormattedTypeParam(LISTING_TYPES.PRESS_RELEASES) === typeParam)
      typeParam = LISTING_TYPES.PRESS_RELEASES
    else if (urlFormattedTypeParam(LISTING_TYPES.MEDIA_COVERAGE) === typeParam)
      typeParam = LISTING_TYPES.MEDIA_COVERAGE
    else typeParam = ''

    return [yearParam, typeParam]
  }

  // Initial page load, set the type and year selection if any
  useEffect(() => {
    let [yearParam, typeParam] = getQueryParams()

    handleFilterItemClick(
      typeParam,
      // Get array form of years from query value
      yearParam && yearParam.split(',').length ? yearParam.split(',') : [],
      false
    )
  }, [])

  const urlFormattedTypeParam = (type: string) => {
    return type?.split(' ')?.join('-')?.toLowerCase()
  }

  const urlFormattedYearParam = (years: string[]) => {
    return years.length ? years.join(',') : ''
  }

  const handleFilterItemClick = (
    type: string | null,
    year: string[] | null,
    wasClicked: boolean
  ) => {
    if (type == '') setTypeSelection(LISTING_TYPES.ALL_ARTICLES)
    else if (type != null) setTypeSelection(type)
    if (year != null) setYearSelection(year)
    // wasClicked will be false if the page was loaded with the filter params, in that case we don't need to update URL
    if (wasClicked) {
      updateURL(year ? year : yearSelection, type || typeSelection)
    }
  }

  useEffect(() => {
    handleListFiltering()
  }, [yearSelection, typeSelection])

  listingPageContent = (
    <GlobalContainer>
      <styles.FeaturedListingOuter featuredListing={true}>
        {featuredListings}
      </styles.FeaturedListingOuter>
      <styles.ListingContainerTopSection>
        <styles.ListingContainerTopButtons>
          {Object.values(LISTING_TYPES).map((type: string, index: number) => {
            return (
              <button
                // onClick={() => setTypeSelection(type)}
                onClick={() => handleFilterItemClick(type, null, true)}
                className={`Listing__Buttons-Styles ${
                  typeSelection.toLowerCase().split(' ').join('-') ===
                  type.toLocaleLowerCase().split(' ').join('-')
                    ? 'active'
                    : ''
                }`}
                key={`Listing__Buttons--${index}`}>
                <Typography type="label">{type}</Typography>
              </button>
            )
          })}
        </styles.ListingContainerTopButtons>
        <styles.ListingContainerTopDropDowns>
          <Select
            listingYears={listingYears}
            setTypeSelection={setTypeSelection}
            setYearSelection={setYearSelection}
            handleFilterItemClick={handleFilterItemClick}
            listingTypes={LISTING_TYPES}
            mobileYearFilterText={mobileYearFilterText}
            typeSelection={typeSelection}
            yearSelection={yearSelection}
          />
        </styles.ListingContainerTopDropDowns>
      </styles.ListingContainerTopSection>
      <styles.ListingContainerBottomSection>
        <styles.ListingContainerLeftSection>
          <Typography type="header6" className="Listing__Year-title">
            {microcopy.values && Array.isArray(microcopy.values) && microcopy.values[4]
              ? microcopy.values[4].value
              : 'Years'}
          </Typography>
          <div className="Listing__Year-Container">
            {listingYears.map((year: any, index: number) => {
              return (
                <label htmlFor={year} key={`listing_year--${index}`}>
                  <input
                    id={year}
                    type="checkbox"
                    value={year}
                    onChange={e => handleFilterItemClick(null, handleYearChange(e), true)}
                    checked={yearSelection.includes(year)}
                  />
                  <Typography type="body" className="listing_year--option-text">
                    {year}
                  </Typography>
                </label>
              )
            })}
          </div>

          <div className="Financial_News-Block">
            <Typography type="bodyXS" className="financial_news-text">
              {financialNewsText}
            </Typography>
            <Button
              icon={true}
              href={financialNewsLinkUrl}
              text={financialNewsLinkText}
              type="text-link"
              size="small"
              theme={'light'}
            />
          </div>
        </styles.ListingContainerLeftSection>
        <styles.ListingContainerRightSection>
          {filteredListings.slice(start, end).map((listing: any, index: number) => {
            let listingTitle = listing.title || '',
              listingDate = listing.date || '',
              listingSlug = listing.slug || '',
              listingUrl = listing.url1 || '',
              listingType = listing.articleType || LISTING_TYPES.MEDIA_COVERAGE,
              listingPublication =
                listing.metaInformation && listing.metaInformation[0]
                  ? listing.metaInformation[0].customerName
                  : ''
            const date = new Date(listingDate)
            let formattedDate = moment(date).utc().format('MMMM DD, YYYY')
            formattedDate = new Date(formattedDate).toLocaleDateString(locale, OPTIONS)

            return (
              <ListingItem
                key={`ListItem--${listingSlug}-${index}`}
                {...{
                  listingTitle,
                  listingSlug,
                  listingUrl,
                  listingType,
                  listingPublication,
                  formattedDate,
                }}
              />
            )
          })}
          <styles.PaginationContainer>
            {filteredListings.length > listingsPerPage && (
              <Pagination
                activePage={getPageNumber()}
                hrefForPage={hrefForPage}
                handleClick={handlePaginationClick}
                numPagesBeforeEllipses={3}
                numResults={filteredListings.length}
                numResultsPerPage={listingsPerPage}
              />
            )}
          </styles.PaginationContainer>
        </styles.ListingContainerRightSection>
      </styles.ListingContainerBottomSection>
    </GlobalContainer>
  )

  const defaultSlug = pageData && pageData.slug ? pageData.slug : '/'
  const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null

  // officePage

  if (pageData && pageData.useCase === 'Offices') {
    let allButtons: { label: string }[] = []
    let totalOffices = 0
    if (tabOrder && tabOrder.items) {
      allButtons = tabOrder.items.map(tab => {
        totalOffices += tab.officeOrder ? tab.officeOrder.length : 0
        return {
          label: `${tab.title}${tab.officeOrder ? `\u00A0(${tab.officeOrder.length})` : ''}`,
        }
      })
      if (microcopy && microcopy.values) {
        allButtons.unshift({
          label:
            findTarget(`AllTAB.text (${totalOffices})`, microcopy.values, false) ||
            `All Offices (${totalOffices})`,
        })
      }
    }
    const [activeRegion, setActiveRegion] = useState(0)

    officesPageContent = (
      <GlobalContainer>
        <SimpleNav
          activeButton={activeRegion}
          setActiveButton={setActiveRegion}
          allButtons={allButtons}
        />
        <styles.AllTabOrderWrapper>
          {tabOrder?.items?.map((region: any, index: number) => {
            const officeRegionData = {
              ...region,
              microcopy,
              index,
              activeRegion,
            }
            return <OfficeRegion key={`OfficeRegion_${index}`} {...officeRegionData} />
          })}
        </styles.AllTabOrderWrapper>
      </GlobalContainer>
    )
  }

  // leadershipPage
  if (pageData && pageData.useCase === 'Leadership') {
    leadershipBoardData = { ...tabOrder }

    if (microcopy && microcopy.values) {
      leadershipBoardData = { ...leadershipBoardData, microcopy }
    }

    leadershipPageContent = (
      <GlobalContainer>
        <styles.AllTabOrderWrapper>
          {tabOrder && tabOrder.items && <LeadershipBoard {...leadershipBoardData} />}
        </styles.AllTabOrderWrapper>
      </GlobalContainer>
    )
  }

  const pageInner = (
    <styles.InnerWrapper data-cy={`CompanyListing`} useCase={pageData.useCase}>
      <SEOTags
        seoMetadata={seoData}
        locale={defaultLocale}
        slug={slug}
        path={slug}
        isPaginated={true}
        pageNum={currentPage}
        hrefForPage={hrefForPage}
        isLastPage={currentPage === numberOfPages}
        availableLocales={pageContext.availableLocales}
        // siteSection="Company Listing"
      />
      {companyListingPageHero}
      {pageData.useCase === 'Newsroom' && listingPageContent}
      {pageData.useCase === 'Offices' && officesPageContent}
      {pageData.useCase === 'Leadership' && leadershipPageContent}
      {dynamicPageContent}
    </styles.InnerWrapper>
  )
  return (
    <Layout.Template
      lang={defaultLocale}
      pageType={pageType}
      slug={defaultSlug}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      availableLocales={pageContext.availableLocales}>
      {pageInner}
    </Layout.Template>
  )
}

export default CompanyListingPage

export const Select = (props: any) => {
  const yearOptions = props.listingYears
  const listingTypes = props.listingTypes
  const mobileYearFilterText = props.mobileYearFilterText
  const typeOptions = [listingTypes.PRESS_RELEASES, listingTypes.MEDIA_COVERAGE]

  // state
  const [typeFilter, setTypeFilter] = useState(props.typeSelection)

  const [isTypeFilterActive, setIsTypeFilterActive] = useState(false)
  const [yearFilter, setYearFilter] = useState(props.yearSelection[0])
  const [isYearFilterActive, setIsYearFilterActive] = useState(false)

  // handles state transitions from buttons to dropdown
  useEffect(() => {
    setTypeFilter(props.typeSelection)
    setYearFilter(props.yearSelection[0])
  }, [props.typeSelection, props.yearSelection])

  const handleFilterClick = (e: any) => {
    e.preventDefault()
    setIsTypeFilterActive(false)
    setIsYearFilterActive(false)
    if (e.target.id === 'type' || e.target.parentElement.id === 'type') {
      setIsTypeFilterActive(!isTypeFilterActive)
    } else if (e.target.id === 'year' || e.target.parentElement.id === 'year') {
      setIsYearFilterActive(!isYearFilterActive)
    }
  }

  const handleFilterItemClick = (e: any) => {
    e.preventDefault()
    if (e.target.getAttribute('data-item') === 'type') {
      if (Object.values(listingTypes).includes(e.target.textContent)) {
        setTypeFilter(e.target.textContent)
        props.handleFilterItemClick(e.target.textContent, null, true)
      }
      setIsTypeFilterActive(false)
    } else if (e.target.getAttribute('data-item') === 'year') {
      if (e.target.textContent === mobileYearFilterText) {
        setYearFilter(mobileYearFilterText)
        props.handleFilterItemClick(null, [], true)
      } else {
        setYearFilter(e.target.textContent)
        props.handleFilterItemClick(null, [e.target.textContent], true)
      }
      setIsYearFilterActive(false)
    }
  }

  return (
    <DropdownContainer className="PartnersLocatorFilters">
      <div className="Jobs__FiltersContainer">
        <div className="PartnersLocator__filter">
          <div
            id="type"
            className={`PartnersLocator__filterClosed ${isTypeFilterActive ? 'is-active' : ''}`}
            onClick={handleFilterClick}>
            <span>{typeFilter ? typeFilter : 'All Articles'}</span>
          </div>
          <div className={`PartnersLocator__filterOpened ${isTypeFilterActive ? 'is-active' : ''}`}>
            <ul>
              <li key="All_Articles_1" data-item="type" onClick={handleFilterItemClick}>
                {/* All Articles */}
                {listingTypes.ALL_ARTICLES}
              </li>
              {typeOptions &&
                typeOptions.map((type: any, index: any) => (
                  <li key={`type_${index}`} data-item="type" onClick={handleFilterItemClick}>
                    {type}
                  </li>
                ))}
            </ul>
          </div>
        </div>
        <div className="PartnersLocator__filter">
          <div
            id="year"
            className={`PartnersLocator__filterClosed ${isYearFilterActive ? 'is-active' : ''}`}
            onClick={handleFilterClick}>
            <span>{yearFilter ? yearFilter : mobileYearFilterText}</span>
          </div>
          <div className={`PartnersLocator__filterOpened ${isYearFilterActive ? 'is-active' : ''}`}>
            <ul>
              <li key="All_Years_1" data-item="year" onClick={handleFilterItemClick}>
                {mobileYearFilterText}
              </li>
              {yearOptions &&
                yearOptions.map((year: any, index: any) => (
                  <li key={`year_${index}`} data-item="year" onClick={handleFilterItemClick}>
                    {year}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </DropdownContainer>
  )
}

// this is a comment to change to trigger a build
